
import { Component, Vue, Watch, Prop, ProvideReactive, Provide } from "vue-property-decorator"
import { namespace, Getter, Mutation, Action, State } from "vuex-class"

import api from "@/services/api"
import FileService from "@/services/file-service"
import { MapProvider } from "@/services/mapProvider"

import { CaseError } from "@/types/caseError"

import SkeletonLoader from "@/components/SkeletonLoader.vue"
import LayoutHost from "@/components/CaseDetails/Layout/LayoutHost.vue"
import DataForm from "@/components/Config/DataForm.vue"
import VuePerfectScrollbar from "vue-perfect-scrollbar"

import { CaseDetails, CaseSummary, Template, TemplateDetails, User } from "@/types"

import ScheduleEditService from "@/services/scheduleEditService"

import {
	applyTemplate
} from "@/scripts/caseDetails"
import { Schedule } from "@/types/schedule"
import { cloneDeep } from "lodash"
import { JsonHubProtocol } from "@aspnet/signalr"
import { CurrentUser } from "@/types/user"
import { TenantSetting } from "@/types/tenantSetting"
const caseStore = namespace("cases")

@Component({
	components: {
		"layout-host": LayoutHost,
		"desktop": () => import("@/components/CaseDetails/Layout/Desktop.vue"),
		"skeleton": SkeletonLoader,
		"data-form": DataForm,
		"scrollbar": VuePerfectScrollbar
	}
})
export default class ScheduleInstance extends Vue {
	@Provide("mapProvider") mapProvider: MapProvider = new MapProvider()
	@Provide("fileService") fileService: FileService = new FileService(this.caseFilesChanged)

	@Prop({ default: null })
	value!: {
		schedule: Schedule,
		date: Date
	} | null

	@Prop({ default: true })
	allowScheduleEdit!: boolean

	@Getter("getMe") public me!: CurrentUser
	@caseStore.Action public detailsLoad!: () => Promise<void>;

	@Getter("getCaseName") getCaseName!: string;
	@Getter("getRecordName") getRecordName!: string;
	@State tenantSettings!: { [key: string]: boolean } | null

	private currentCase: CaseDetails | null = null
	private currentTemplate: TemplateDetails | null = null

	private templates: { [key:string]: TemplateDetails } = {}

	private scheduleCase: CaseDetails | null = null
	private scheduleView: boolean = false

	private editSchedule: Schedule | null = null
	private scheduleEdit: ScheduleEditService | null = new ScheduleEditService()

	saving: boolean = false
	valid: boolean = true

	get open (): boolean {
		return !!this.value
	}

	set open (value: boolean) {
		if (!value) {
			this.scheduleView = false
			this.$emit("input", null)
		}
	}

	get caseTitle () {
		if (this.value) {
			return this.value.schedule.title
		}
		if (!this.currentCase) return ""
		return `${this.currentCase.templateTitle} ${this.currentCase.isCase ? this.getCaseName : this.getRecordName}`
	}

	get canEditSchedules () {
		return this.me && (this.me.editData || (this.me.extendedPermissions && this.me.extendedPermissions["scheduling"]))
	}

	private get scheduleColumns () {
		if (!this.scheduleEdit) return []

		return this.scheduleEdit.columns.filter((c: any) => {
			if (c.key === "endTime") {
				return !this.tenantSettings || !this.tenantSettings["scheduleHideTimeSpan"]
			}
			if (c.key === "bulkCreateForTeams") {
				return !this.tenantSettings || !this.tenantSettings["scheduleHideBulkCreate"]
			}
			return true
		})
	}

	@Watch("value")
	async valueUpdated (newValue: { schedule: Schedule, date: Date } | null) {
		if (newValue && newValue.schedule) {
			this.detailsLoad()
			this.editSchedule = cloneDeep(newValue.schedule)

			api.scheduleInstance(newValue.schedule.id!, newValue.date).then(({ scheduleInstance, updatedScheduleInstance }) => {
				// delete updatedScheduleInstance.created
				// delete scheduleInstance.created

				this.currentCase = updatedScheduleInstance
				this.scheduleCase = scheduleInstance

				if (updatedScheduleInstance.templateId) {
					// Load template - use cached values if present
					if (this.templates[updatedScheduleInstance.templateId]) {
						this.currentTemplate = this.templates[updatedScheduleInstance.templateId]
					} else {
						api.templateDetails(updatedScheduleInstance.templateId!).then(template => {
							this.currentTemplate = this.templates[updatedScheduleInstance.templateId!] = template
						})
					}
				}
			})
		} else {
			this.currentCase = null
			this.scheduleCase = null
			this.editSchedule = null
			this.currentTemplate = null
		}
	}

	mounted () {
		this.scheduleEdit!.load(this.me)
		this.valueUpdated(this.value)
	}

	private close () {
		this.$emit("input", null)
	}

	private caseFilesChanged () {
	}

	private async save () {
		if (this.scheduleView) {
			if (this.value && this.editSchedule) {
				this.scheduleEdit!.saveSchedule(this.editSchedule)
				this.$emit("scheduleUpdated", this.editSchedule)
			}
		} else {
			if (this.value && this.currentCase && this.scheduleCase) {
				const instanceFieldvalues: { [key: string]: any } = {}

				// For each property, check the updated model against the original. Test for equality using JSON serialization. If something doesn't serialize the same, then it has been modified.
				Object.keys(this.currentCase).forEach(field => {
					const fieldKey = field as keyof CaseDetails
					if (field === "fields") {
						Object.keys(this.currentCase!.fields).forEach(fieldId => {
							if (JSON.stringify(this.currentCase!.fields[fieldId].value) !== JSON.stringify(this.scheduleCase!.fields[fieldId].value)) {
								instanceFieldvalues["fields." + fieldId] = {
									value: this.currentCase!.fields[fieldId].value,
									label: this.currentCase!.fields[fieldId].label
								}
							}
						})
					} else {
						if (JSON.stringify(this.currentCase![fieldKey]) !== JSON.stringify(this.scheduleCase![fieldKey])) {
							instanceFieldvalues[field] = {
								value: this.currentCase![fieldKey],
								label: null
							}
						}
					}
				})

				await api.scheduleInstanceSet(this.value.schedule.id!, this.value.date, instanceFieldvalues)
				this.$emit("scheduleInstanceUpdated", {
					scheduleId: this.value.schedule.id,
					date: this.value.date,
					fieldValues: instanceFieldvalues
				})
			}
		}

		this.$emit("input", null)
		this.scheduleView = false
	}
}

